<template>
   <div class="dynamic-variables-page">
      <!-- ── Page Header ── -->
      <div class="ms-page-header">
         <div class="ms-page-header__content d-flex justify-content-between align-items-center flex-wrap gap-3 w-100">
            <div class="d-flex align-items-center gap-3">
               <div class="ms-page-header__icon">
                  <i class="fas fa-code"></i>
               </div>
               <div>
                  <p class="ms-page-header__breadcrumb">Platform Settings <span class="ms-breadcrumb-sep">/</span> Dynamic Variables</p>
                  <div class="d-flex align-items-center gap-2 mt-1">
                     <h1 class="ms-page-header__title">Dynamic Variables</h1>
                     <span class="badge bg-warning text-dark fw-bold">EXPERIMENTAL</span>
                  </div>
               </div>
            </div>
         </div>
      </div>

      <div class="row">
         <div class="col-md-3">
            <div class="card shadow-sm border-0 mb-4">
               <div class="card-body">
                  <h5 class="card-title fw-bold">Variable Groups</h5>
                  <p class="text-muted small">Select a group to manage its dynamic variables. These variables will be injected where appropriate across the platform.</p>
                  
                  <ul class="list-group list-group-flush group-list mt-3">
                     <li v-for="group in variableGroups" :key="group.id" 
                         class="list-group-item list-group-item-action d-flex justify-content-between align-items-center"
                         :class="{ 'active-group': selectedGroup.id === group.id }"
                         @click="selectGroup(group)"
                         style="cursor: pointer; border-radius: 8px; margin-bottom: 5px;">
                        <div>
                           <i :class="group.icon + ' me-2'"></i>
                           {{ group.name }}
                        </div>
                        <span class="badge bg-secondary rounded-pill">{{ group.variables.length }}</span>
                     </li>
                  </ul>
                  
                  <button class="btn btn-outline-info w-100 mt-3" disabled title="Not implemented in experimental stage">
                     <i class="fa fa-plus me-1"></i> Add Group
                  </button>
               </div>
            </div>
         </div>

         <div class="col-md-9">
            <div class="card shadow-sm border-0">
               <div class="card-header bg-white border-bottom-0 pt-4 pb-0 d-flex justify-content-between align-items-center">
                  <h5 class="fw-bold mb-0">
                     <i :class="selectedGroup.icon + ' me-2 text-info'"></i>
                     {{ selectedGroup.name }} Variables
                  </h5>
                  <button v-if="$store.getters['auth/isGlobalSuperAdmin']" class="btn btn-info text-white fw-bold btn-sm" @click="openAddModal">
                     <i class="fa fa-plus me-1"></i> Add Variable
                  </button>
               </div>
               <div class="card-body">
                  <p class="text-muted small mb-4">
                     Manage dynamic variables for <strong>{{ selectedGroup.name }}</strong>. Click the copy icon to copy the variable tag for use in templates.
                  </p>

                  <div class="table-responsive">
                     <table class="table align-middle table-hover">
                        <thead>
                           <tr>
                              <th>Variable Name</th>
                              <th>Merge Tag</th>
                              <th>Data Type</th>
                              <th>Preview/Status</th>
                              <th v-if="$store.getters['auth/isGlobalSuperAdmin']" class="text-end">Action</th>
                           </tr>
                        </thead>
                        <tbody>
                           <tr v-if="selectedGroup.variables.length === 0">
                              <td :colspan="$store.getters['auth/isGlobalSuperAdmin'] ? 5 : 4" class="text-center text-muted py-4">
                                 No variables found for this group.
                              </td>
                           </tr>
                           <tr v-for="variable in selectedGroup.variables" :key="variable.id">
                              <td>
                                 <span class="fw-bold">{{ variable.name }}</span>
                                 <div class="text-muted small">{{ variable.description }}</div>
                              </td>
                              <td>
                                 <div class="input-group input-group-sm">
                                    <input type="text" class="form-control bg-light" :value="variable.tag" readonly style="font-family: monospace;">
                                    <button class="btn btn-outline-secondary" type="button" @click="copyTag(variable.tag)" title="Copy tag">
                                       <i class="fa fa-copy"></i>
                                    </button>
                                 </div>
                              </td>
                              <td>
                                 <span class="badge bg-light text-dark border">{{ variable.type }}</span>
                              </td>
                              <td>
                                 <span v-if="variable.isActive" class="badge bg-success bg-opacity-10 text-success"><i class="fa fa-circle me-1" style="font-size: 8px;"></i> Active</span>
                                 <span v-else class="badge bg-secondary bg-opacity-10 text-secondary"><i class="fa fa-circle me-1" style="font-size: 8px;"></i> Inactive</span>
                              </td>
                              <td v-if="$store.getters['auth/isGlobalSuperAdmin']" class="text-end">
                                 <button class="btn btn-sm btn-circle me-2" title="Edit (Disabled in preview)">
                                    <i class="fa fa-pencil"></i>
                                 </button>
                                 <button class="btn btn-sm btn-circle btn-circle-danger" title="Delete (Disabled in preview)">
                                    <i class="fa fa-trash"></i>
                                 </button>
                              </td>
                           </tr>
                        </tbody>
                     </table>
                  </div>
               </div>
            </div>
         </div>
      </div>

      <!-- Add Variable Modal Concept -->
      <div class="modal fade" id="addVariableModal" tabindex="-1" ref="addVariableModal">
         <div class="modal-dialog">
            <div class="modal-content">
               <div class="modal-header">
                  <h5 class="modal-title fw-bold">Add Dynamic Variable</h5>
                  <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
               </div>
               <div class="modal-body">
                  <div class="alert alert-warning py-2 small mb-4">
                     <i class="fa fa-info-circle me-1"></i> This is an experimental concept. Data will not be saved to the database.
                  </div>
                  
                  <form @submit.prevent="saveVariable">
                     <div class="mb-3">
                        <label class="form-label fw-bold">Variable Name</label>
                        <input type="text" class="form-control" v-model="newVariable.name" placeholder="E.g. Client First Name" required>
                     </div>
                     <div class="mb-3">
                        <label class="form-label fw-bold">Merge Tag</label>
                        <div class="input-group">
                           <span class="input-group-text bg-light">{</span>
                           <input type="text" class="form-control" v-model="newVariable.tagKey" placeholder="e.g. client_first_name" required>
                           <span class="input-group-text bg-light">}</span>
                        </div>
                        <div class="form-text">The exact tag to be used in document templates.</div>
                     </div>
                     <div class="mb-3">
                        <label class="form-label fw-bold">Data Type</label>
                        <select class="form-select" v-model="newVariable.type">
                           <option value="String">String (Text)</option>
                           <option value="Number">Number</option>
                           <option value="Date">Date</option>
                           <option value="Table">Table / Grid</option>
                           <option value="Boolean">Yes / No</option>
                        </select>
                     </div>
                     <div class="mb-3">
                        <label class="form-label fw-bold">Description</label>
                        <textarea class="form-control" rows="2" v-model="newVariable.description" placeholder="Short explanation of what this variable represents"></textarea>
                     </div>
                  </form>
               </div>
               <div class="modal-footer bg-light">
                  <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">Cancel</button>
                  <button type="button" class="btn btn-info text-white fw-bold" @click="saveVariable">Save Concept</button>
               </div>
            </div>
         </div>
      </div>

   </div>
</template>

<script>
// Mock data for the experimental concept
const MOCK_GROUPS = [
   {
      id: 'contracts',
      name: 'Contracts',
      icon: 'fa-solid fa-file-signature',
      variables: [
         { id: 1, name: 'Client Company Name', tag: '{client_company}', type: 'String', description: 'The official name of the client\'s organization.', isActive: true },
         { id: 2, name: 'Signer Full Name', tag: '{signer_name}', type: 'String', description: 'Full name of the primary signer.', isActive: true },
         { id: 3, name: 'Contract Start Date', tag: '{contract_start_date}', type: 'Date', description: 'The date the contract becomes active.', isActive: true },
         { id: 4, name: 'Pricing Table', tag: '{contract_pricing_table}', type: 'Table', description: 'Dynamic table displaying selected products and pricing.', isActive: true },
      ]
   },
   {
      id: 'proposals',
      name: 'Proposals',
      icon: 'fa-solid fa-file-contract',
      variables: [
         { id: 5, name: 'Proposal Validity Days', tag: '{proposal_validity}', type: 'Number', description: 'Number of days the proposal is valid for.', isActive: true },
         { id: 6, name: 'BDM Name', tag: '{bdm_name}', type: 'String', description: 'Name of the vertex representative.', isActive: true },
         { id: 7, name: 'Meeting Link', tag: '{bdm_meeting_link}', type: 'String', description: 'Clickable Google Calendar link for the BDM.', isActive: true },
      ]
   },
   {
      id: 'leads',
      name: 'Leads & Clients',
      icon: 'fa-solid fa-users',
      variables: [
         { id: 8, name: 'Primary Contact Email', tag: '{lead_email}', type: 'String', description: 'Email address of the lead.', isActive: true },
      ]
   },
   {
      id: 'system',
      name: 'System Global',
      icon: 'fa-solid fa-globe',
      variables: [
         { id: 9, name: 'Current Year', tag: '{current_year}', type: 'Number', description: 'The current calendar year (e.g. for copyright).', isActive: true },
      ]
   }
];

export default {
   name: 'DynamicVariablesDashboard',
   data() {
      return {
         variableGroups: MOCK_GROUPS,
         selectedGroup: MOCK_GROUPS[0],
         modalInstance: null,
         newVariable: {
            name: '',
            tagKey: '',
            type: 'String',
            description: ''
         }
      }
   },
   mounted() {
      if (typeof window.bootstrap !== 'undefined') {
         this.modalInstance = new window.bootstrap.Modal(this.$refs.addVariableModal, {
            backdrop: 'static'
         });
      }
   },
   methods: {
      selectGroup(group) {
         this.selectedGroup = group;
      },
      openAddModal() {
         this.newVariable = {
            name: '',
            tagKey: '',
            type: 'String',
            description: ''
         };
         if (this.modalInstance) {
            this.modalInstance.show();
         }
      },
      saveVariable() {
         if (!this.newVariable.name || !this.newVariable.tagKey) {
            this.$toast.warning("Please fill in variable name and tag");
            return;
         }

         const newVar = {
            id: Date.now(),
            name: this.newVariable.name,
            tag: `{${this.newVariable.tagKey.replace(/[^a-zA-Z0-9_]/g, '')}}`,
            type: this.newVariable.type,
            description: this.newVariable.description,
            isActive: true
         };

         this.selectedGroup.variables.push(newVar);
         
         if (this.modalInstance) {
            this.modalInstance.hide();
         }
         this.$toast.success("Variable concept added!");
      },
      copyTag(tag) {
         navigator.clipboard.writeText(tag).then(() => {
            this.$toast.success("Copied to clipboard!");
         }).catch(() => {
            this.$toast.error("Failed to copy");
         });
      }
   }
}
</script>

<style scoped>
.active-group {
   background-color: #f0f9ff;
   border-left: 4px solid #00a0c1 !important;
   color: #00a0c1;
   font-weight: 600;
}

.active-group .badge {
   background-color: #00a0c1 !important;
}

.list-group-item:not(.active-group):hover {
   background-color: #f8f9fa;
}

.btn-circle {
   width: 30px;
   height: 30px;
   padding: 0;
   border-radius: 50%;
   display: inline-flex;
   align-items: center;
   justify-content: center;
   background-color: #f1f3f5;
   color: #495057;
   border: none;
   transition: all 0.2s;
}

.btn-circle:hover {
   background-color: #e2e6ea;
   color: #212529;
}

.btn-circle-danger {
   background-color: #fff5f5;
   color: #dc3545;
}

.btn-circle-danger:hover {
   background-color: #f8d7da;
   color: #bd2130;
}

/* Page Header Styles */
.ms-page-header {
   margin-bottom: 24px;
   padding: 20px 24px;
   background: linear-gradient(135deg, #ffffff 0%, #f4f9fb 100%);
   border: 1px solid rgba(0, 130, 152, 0.15);
   border-left: 5px solid #008298;
   border-radius: 12px;
   box-shadow: 0 4px 18px rgba(0, 78, 90, 0.05);
}

.ms-page-header__content {
   display: flex;
   align-items: center;
   gap: 16px;
}

.ms-page-header__icon {
   width: 44px;
   height: 44px;
   border-radius: 10px;
   background: rgba(0, 130, 152, 0.08);
   display: flex;
   align-items: center;
   justify-content: center;
   font-size: 1.15rem;
   color: #008298;
}

.ms-page-header__title {
   font-size: 1.45rem;
   font-weight: 800;
   color: #00363f;
   margin: 0;
   letter-spacing: -0.3px;
}

.ms-page-header__breadcrumb {
   font-size: 0.76rem;
   color: #557d84;
   font-weight: 700;
   text-transform: uppercase;
   letter-spacing: 0.8px;
   margin-bottom: 4px;
}

.ms-breadcrumb-sep {
   margin: 0 6px;
   color: rgba(0, 130, 152, 0.3);
}
</style>
